import { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Target,
    Truck,
    Package,
    AlertTriangle,
    Clock,
    DollarSign,
    MapPin,
    RefreshCw,
    Shield,
    ShieldAlert,
    Zap,
    Activity,
    FastForward,
    RotateCcw,
    Loader2,
    ArrowRight,
    BookCheck,
    Building2,
    Flame,
    CheckCircle,
    Info,
    Route,
    Calendar,
    Timer,
    Bell,
    Star,
    TrendingUp,
    Maximize2,
    Minimize2,
    TrendingDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Calculate Pickup ETA based on deadhead miles and HOS
const calculatePickupETA = (load, hosState) => {
    const deadheadMiles = load.original_deadhead_miles || load.deadhead_miles || 50;
    const avgSpeed = 55; // mph
    const driveHoursAvailable = hosState?.drive_hours_remaining || 11;
    
    const hoursToPickup = deadheadMiles / avgSpeed;
    
    if (hoursToPickup <= driveHoursAvailable) {
        const eta = new Date();
        eta.setHours(eta.getHours() + hoursToPickup);
        return eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
        // Need a break
        const totalHours = hoursToPickup + 10; // Add 10hr break
        const eta = new Date();
        eta.setHours(eta.getHours() + totalHours);
        return eta.toLocaleDateString([], { weekday: 'short' }) + ' ' + 
               eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
};

// Calculate Delivery ETA based on load miles and HOS
const calculateDeliveryETA = (load, hosState) => {
    const deadheadMiles = load.original_deadhead_miles || load.deadhead_miles || 50;
    const loadMiles = load.distance || 0;
    const totalMiles = deadheadMiles + loadMiles;
    const avgSpeed = 55; // mph
    const unloadTime = 2; // hours for unloading
    const driveHoursAvailable = hosState?.drive_hours_remaining || 11;
    
    const totalDriveHours = totalMiles / avgSpeed;
    const breaksNeeded = Math.floor(totalDriveHours / 11);
    const totalHours = totalDriveHours + (breaksNeeded * 10) + unloadTime;
    
    const eta = new Date();
    eta.setHours(eta.getHours() + totalHours);
    
    if (totalHours > 24) {
        return eta.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    } else {
        return eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
};

// Check if load violates policy thresholds
const checkPolicyViolation = (load, settings) => {
    // Hard limits enforced at API level: credit < 80 OR days > 45 are filtered out
    // This check is for softer policy warnings based on user settings
    const minCreditScore = settings?.min_broker_credit_score || 80;
    const maxDaysToPay = settings?.max_broker_days_to_pay || 45;
    
    const creditViolation = load.broker_credit_score !== null && 
                           load.broker_credit_score !== undefined && 
                           load.broker_credit_score < minCreditScore;
    const daysViolation = load.broker_days_to_pay !== null && 
                         load.broker_days_to_pay !== undefined && 
                         load.broker_days_to_pay > maxDaysToPay;
    
    return {
        hasViolation: creditViolation || daysViolation,
        creditViolation,
        daysViolation
    };
};

// Load signal thresholds - respects policy before labeling HOT
const getLoadSignal = (load, truck, settings) => {
    // First check for policy violations
    const policy = checkPolicyViolation(load, settings);
    
    if (policy.hasViolation) {
        return { 
            signal: 'POLICY', 
            icon: ShieldAlert, 
            color: 'text-red-500', 
            bg: 'bg-red-500/10 border-red-500/30',
            label: '🚫 Policy Violation'
        };
    }
    
    // Only evaluate HOT/GOOD/LOW for policy-compliant loads
    const weeklyTarget = truck?.weekly_target_revenue || 6500;
    const minFloor = truck?.min_weekly_floor || 5000;
    const dailyTarget = weeklyTarget / 7;
    const loadValue = load.gross_pay || 0;
    
    if (loadValue >= dailyTarget * 1.2) {
        return { signal: 'HOT', icon: Flame, color: 'text-orange-500', bg: 'bg-orange-500/10 border-orange-500/30', label: '🔥 HOT' };
    } else if (loadValue >= dailyTarget * 0.8) {
        return { signal: 'GOOD', icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10 border-green-500/30', label: '🟢 GOOD' };
    } else {
        return { signal: 'LOW', icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-500/10 border-amber-500/30', label: '⚠️ LOW' };
    }
};

// Risk level helper
const getRiskLevel = (score, threshold, isHigherBetter = true) => {
    if (score === null || score === undefined) return { level: 'unknown', color: 'bg-zinc-500' };
    if (isHigherBetter) {
        if (score >= threshold) return { level: 'low', color: 'bg-green-500' };
        if (score >= threshold * 0.7) return { level: 'medium', color: 'bg-amber-500' };
        return { level: 'high', color: 'bg-red-500' };
    } else {
        if (score <= threshold) return { level: 'low', color: 'bg-green-500' };
        if (score <= threshold * 1.5) return { level: 'medium', color: 'bg-amber-500' };
        return { level: 'high', color: 'bg-red-500' };
    }
};

// Priority tier colors
const getTierColor = (tier) => {
    switch (tier) {
        case 'EMPTY_NOW': return 'bg-red-500 text-white';
        case '<6_HOURS': return 'bg-orange-500 text-white';
        case '<12_HOURS': return 'bg-amber-500 text-black';
        case 'COVERED': return 'bg-green-500 text-white';
        default: return 'bg-zinc-500 text-white';
    }
};

// Format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount || 0);
};

// Format date/time
const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + 
               ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return dateStr;
    }
};

// Format hours
const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '--';
    return `${hours.toFixed(1)}h`;
};

// =====================================================
// FREIGHT HUB DENSITY SCORES (0-100)
// Higher scores indicate better freight availability
// =====================================================
const FREIGHT_HUB_SCORES = {
    // Tier 1: Major Freight Hubs (90-100)
    'chicago': 98, 'los angeles': 97, 'dallas': 96, 'atlanta': 95,
    'houston': 94, 'memphis': 93, 'indianapolis': 92, 'columbus': 91,
    'louisville': 90, 'kansas city': 90,
    // Tier 2: Strong Markets (80-89)
    'phoenix': 89, 'denver': 88, 'nashville': 87, 'charlotte': 86,
    'detroit': 85, 'st. louis': 85, 'philadelphia': 84, 'cincinnati': 84,
    'cleveland': 83, 'jacksonville': 82, 'tampa': 81, 'orlando': 80,
    // Tier 3: Good Markets (70-79)
    'san antonio': 79, 'austin': 78, 'san diego': 77, 'las vegas': 76,
    'minneapolis': 75, 'omaha': 75, 'oklahoma city': 74, 'raleigh': 73,
    'baltimore': 72, 'pittsburgh': 71, 'fresno': 70, 'sacramento': 70,
    // Tier 4: Moderate Markets (60-69)
    'salt lake city': 69, 'albuquerque': 68, 'tucson': 67, 'baton rouge': 66,
    'new orleans': 65, 'portland': 64, 'san francisco': 63, 'boston': 62,
    'new york': 61, 'miami': 60,
    // Tier 5: Challenging Markets (<60)
    'seattle': 55, 'richmond': 54, 'norfolk': 52
};

// Get hub score for a city (defaults to 50 for unknown cities)
const getHubScore = (city) => {
    if (!city) return 50;
    const normalizedCity = city.toLowerCase().trim();
    return FREIGHT_HUB_SCORES[normalizedCity] || 50;
};

// =====================================================
// LOAD SCORING SYSTEM
// =====================================================
const calculateLoadScore = (load, truck) => {
    let score = 0;
    const breakdown = {};
    
    // 1. RPM SCORE (max 25 points)
    const rpm = load.distance > 0 ? load.gross_pay / load.distance : 0;
    if (rpm >= 2.50) {
        breakdown.rpm = 25;
    } else if (rpm >= 2.20) {
        breakdown.rpm = 20;
    } else if (rpm >= 2.00) {
        breakdown.rpm = 15;
    } else if (rpm >= 1.80) {
        breakdown.rpm = 10;
    } else {
        breakdown.rpm = 5;
    }
    score += breakdown.rpm;
    
    // 2. HUB SCORE (max 20 points) - based on destination hub quality
    const hubScore = getHubScore(load.destination_city);
    if (hubScore >= 90) {
        breakdown.hub = 20;
    } else if (hubScore >= 80) {
        breakdown.hub = 15;
    } else if (hubScore >= 70) {
        breakdown.hub = 10;
    } else {
        breakdown.hub = 3;
    }
    score += breakdown.hub;
    
    // 3. DEADHEAD SCORE (max 15 points)
    const deadheadMiles = load.original_deadhead_miles || load.deadhead_miles || 999;
    if (deadheadMiles <= 50) {
        breakdown.deadhead = 15;
    } else if (deadheadMiles <= 100) {
        breakdown.deadhead = 10;
    } else if (deadheadMiles <= 200) {
        breakdown.deadhead = 5;
    } else {
        breakdown.deadhead = 0;
    }
    score += breakdown.deadhead;
    
    // 4. PREFERRED LANE SCORE (max 20 points)
    breakdown.preferredLane = 0;
    if (truck?.preferred_lanes && truck.preferred_lanes.length > 0) {
        const loadOrigin = `${load.origin_city?.toLowerCase()},${load.origin_state?.toLowerCase()}`;
        const loadDest = `${load.destination_city?.toLowerCase()},${load.destination_state?.toLowerCase()}`;
        
        for (const lane of truck.preferred_lanes) {
            if (!lane.origin_city && !lane.destination_city) continue;
            
            const laneOrigin = `${lane.origin_city?.toLowerCase()},${lane.origin_state?.toLowerCase()}`;
            const laneDest = `${lane.destination_city?.toLowerCase()},${lane.destination_state?.toLowerCase()}`;
            
            // Exact match: both origin and destination match
            if (loadOrigin === laneOrigin && loadDest === laneDest) {
                breakdown.preferredLane = 20;
                break;
            }
            // Partial match: load moves toward preferred lane destination
            else if (loadDest === laneDest || loadDest === laneOrigin) {
                breakdown.preferredLane = Math.max(breakdown.preferredLane, 10);
            }
        }
    }
    score += breakdown.preferredLane;
    
    // 5. REVENUE GOAL SCORE (max 15 points)
    const rate = load.gross_pay || 0;
    if (rate >= 2000) {
        breakdown.revenue = 15;
    } else if (rate >= 1500) {
        breakdown.revenue = 10;
    } else if (rate >= 1200) {
        breakdown.revenue = 5;
    } else {
        breakdown.revenue = 0;
    }
    score += breakdown.revenue;
    
    // 6. RISK PENALTIES
    breakdown.riskPenalty = 0;
    
    // Broker credit score penalty
    const creditScore = load.broker_credit_score || 100;
    if (creditScore < 90) {
        breakdown.riskPenalty -= 20;
    }
    
    // Days to pay penalty
    const daysToPay = load.broker_days_to_pay || 0;
    if (daysToPay > 30) {
        breakdown.riskPenalty -= 10;
    }
    
    score += breakdown.riskPenalty;
    
    // Ensure score doesn't go negative
    score = Math.max(0, score);
    
    return {
        totalScore: score,
        breakdown,
        rpm: rpm.toFixed(2),
        hubScore: hubScore
    };
};

// =====================================================
// MARKET RATE CALCULATION
// =====================================================
const calculateMarketRate = (load) => {
    // Base market rate estimation using lane characteristics
    // Average RPM varies by region and equipment type
    const baseRpm = 2.50; // National average baseline
    
    // Adjust for distance (shorter hauls typically have higher RPM)
    const distanceFactor = load.distance < 300 ? 1.15 : load.distance < 600 ? 1.05 : 1.0;
    
    // Adjust for pickup urgency (same-day pickups command premium)
    const urgencyFactor = 1.0; // TODO: Calculate from pickup_date
    
    const estimatedMarketRpm = baseRpm * distanceFactor * urgencyFactor;
    const offeredRpm = load.distance > 0 ? load.gross_pay / load.distance : 0;
    const difference = offeredRpm - estimatedMarketRpm;
    
    // Negotiation target is market rate + 5% premium
    const negotiationTarget = estimatedMarketRpm * 1.05;
    
    return {
        marketRpm: estimatedMarketRpm,
        offeredRpm: offeredRpm,
        difference: difference,
        negotiationTarget: negotiationTarget,
        isAboveMarket: difference >= 0
    };
};

// Market Rate Panel Component
function MarketRatePanel({ load }) {
    const marketData = calculateMarketRate(load);
    
    return (
        <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50">
            <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wide">Market Rate</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                    <span className="text-zinc-500 text-xs">Market Rate</span>
                    <p className="text-white font-mono">${marketData.marketRpm.toFixed(2)} / mi</p>
                </div>
                <div>
                    <span className="text-zinc-500 text-xs">Offered Rate</span>
                    <p className="text-white font-mono">${marketData.offeredRpm.toFixed(2)} / mi</p>
                </div>
                <div>
                    <span className="text-zinc-500 text-xs">Difference</span>
                    <p className={`font-mono flex items-center gap-1 ${marketData.isAboveMarket ? 'text-green-400' : 'text-red-400'}`}>
                        {marketData.isAboveMarket ? (
                            <TrendingUp className="w-3 h-3" />
                        ) : (
                            <TrendingDown className="w-3 h-3" />
                        )}
                        {marketData.difference >= 0 ? '+' : ''}{marketData.difference.toFixed(2)} / mi
                    </p>
                </div>
                <div>
                    <span className="text-zinc-500 text-xs">Negotiation Target</span>
                    <p className="text-amber-400 font-mono font-semibold">${marketData.negotiationTarget.toFixed(2)} / mi</p>
                </div>
            </div>
        </div>
    );
}

// Load Score Badge Component
function LoadScoreBadge({ load }) {
    const score = load._score || 0;
    const breakdown = load._scoreBreakdown || {};
    
    // Color based on score
    let bgColor = 'bg-red-600/50 text-red-300';
    if (score >= 70) bgColor = 'bg-green-600/50 text-green-300';
    else if (score >= 50) bgColor = 'bg-amber-600/50 text-amber-300';
    else if (score >= 30) bgColor = 'bg-orange-600/50 text-orange-300';
    
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger>
                    <Badge className={`${bgColor} text-sm font-bold px-2 py-0.5`}>
                        {score} pts
                    </Badge>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 border-zinc-700 w-52">
                    <p className="font-semibold text-white mb-2">Score Breakdown</p>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                            <span className="text-zinc-400">RPM</span>
                            <span className="text-white">{breakdown.rpm || 0} pts</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-400">Hub Quality</span>
                            <span className="text-white">{breakdown.hub || 0} pts</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-400">Deadhead</span>
                            <span className="text-white">{breakdown.deadhead || 0} pts</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-400">Preferred Lane</span>
                            <span className="text-white">{breakdown.preferredLane || 0} pts</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-400">Revenue</span>
                            <span className="text-white">{breakdown.revenue || 0} pts</span>
                        </div>
                        {breakdown.riskPenalty !== 0 && (
                            <div className="flex justify-between text-red-400">
                                <span>Risk Penalty</span>
                                <span>{breakdown.riskPenalty} pts</span>
                            </div>
                        )}
                        <div className="border-t border-zinc-600 pt-1 mt-1 flex justify-between font-semibold">
                            <span className="text-zinc-300">Total</span>
                            <span className="text-white">{score} pts</span>
                        </div>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

// Best Load Card Component - Highlighted primary recommendation
function BestLoadCard({ load, truck, onStage, isLoading, settings }) {
    const signal = getLoadSignal(load, truck, settings);
    const SignalIcon = signal.icon;
    
    const maxDaysToPay = settings?.max_broker_days_to_pay || 30;
    const minCreditScore = settings?.min_broker_credit_score || 90;
    const creditRisk = getRiskLevel(load.broker_credit_score, minCreditScore, true);
    const daysRisk = getRiskLevel(load.broker_days_to_pay, maxDaysToPay, false);
    
    // Calculate rate per mile
    const ratePerMile = load.distance > 0 ? (load.gross_pay / load.distance).toFixed(2) : '--';
    
    return (
        <div 
            className="p-4 rounded-xl border-2 border-blue-500 bg-gradient-to-br from-blue-500/10 to-blue-600/5 ring-2 ring-blue-500/20 shadow-lg shadow-blue-500/10"
            data-testid={`best-load-${load.load_id}`}
        >
            {/* Best Load Badge */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500 text-white">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm font-bold">BEST LOAD</span>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${signal.bg}`}>
                        <SignalIcon className={`w-3 h-3 ${signal.color}`} />
                        <span className={`text-xs font-semibold ${signal.color}`}>{signal.label || signal.signal}</span>
                    </div>
                    <LoadScoreBadge load={load} />
                </div>
                <span className="text-white font-mono text-base font-semibold">{load.load_id?.slice(-8)}</span>
            </div>
            
            {/* Route Info - Larger */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Pickup */}
                <div className="bg-zinc-900/70 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-green-400 text-sm mb-1.5">
                        <MapPin className="w-4 h-4" />
                        <span className="font-medium">PICKUP</span>
                    </div>
                    <p className="text-white text-lg font-semibold">{load.origin_city}, {load.origin_state}</p>
                    <p className="text-zinc-400 text-sm flex items-center gap-1 mt-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {load.pickup_date || '--'} {load.pickup_time || ''}
                    </p>
                </div>
                {/* Delivery */}
                <div className="bg-zinc-900/70 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-blue-400 text-sm mb-1.5">
                        <MapPin className="w-4 h-4" />
                        <span className="font-medium">DELIVERY</span>
                    </div>
                    <p className="text-white text-lg font-semibold">{load.destination_city}, {load.destination_state}</p>
                    <p className="text-zinc-400 text-sm flex items-center gap-1 mt-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {load.delivery_date || '--'} {load.delivery_time || ''}
                    </p>
                </div>
            </div>
            
            {/* Key Metrics - Prominent */}
            <div className="grid grid-cols-4 gap-3 mb-4 bg-zinc-900/50 rounded-lg p-3">
                <div className="text-center">
                    <p className="text-zinc-500 text-xs mb-1">Miles</p>
                    <p className="text-white text-lg font-bold">{load.distance || '--'}</p>
                </div>
                <div className="text-center">
                    <p className="text-zinc-500 text-xs mb-1">Deadhead</p>
                    <p className="text-white text-lg font-bold">{load.original_deadhead_miles?.toFixed(0) || '--'}</p>
                </div>
                <div className="text-center border-l border-r border-zinc-700">
                    <p className="text-zinc-500 text-xs mb-1">Rate</p>
                    <p className="text-green-400 text-xl font-bold">{formatCurrency(load.gross_pay)}</p>
                </div>
                <div className="text-center">
                    <p className="text-zinc-500 text-xs mb-1">$/Mile</p>
                    <p className="text-green-400 text-lg font-bold">${ratePerMile}</p>
                </div>
            </div>
            
            {/* Broker Info */}
            <div className="flex items-center justify-between py-2 border-t border-zinc-700/50 mb-4">
                <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-zinc-500" />
                    <span className="text-zinc-200 font-medium">{load.broker_name || 'Unknown Broker'}</span>
                </div>
                <div className="flex items-center gap-2">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <Badge className={`${creditRisk.color} text-sm px-2`}>
                                    {load.broker_credit_score || '--'}
                                </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="bg-zinc-800 border-zinc-700">
                                <p className="text-xs">Credit Score (min: {minCreditScore})</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <Badge className={`${daysRisk.color} text-sm px-2`}>
                                    {load.broker_days_to_pay || '--'}d
                                </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="bg-zinc-800 border-zinc-700">
                                <p className="text-xs">Days to Pay (max: {maxDaysToPay})</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <Shield className="w-4 h-4 text-green-500" />
                </div>
            </div>
            
            {/* Market Rate Panel */}
            <div className="mb-4">
                <MarketRatePanel load={load} />
            </div>
            
            {/* Primary Stage Button - Sends to Operations */}
            <Button
                size="lg"
                onClick={() => onStage(load.load_id, truck.truck_id)}
                disabled={isLoading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold text-base h-12"
                data-testid={`stage-best-${truck.truck_id}-${load.load_id}`}
            >
                {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                    <ArrowRight className="w-5 h-5 mr-2" />
                )}
                Stage Load → Operations
            </Button>
        </div>
    );
}

// Alternate Load Card Component - Compact backup option
function AlternateLoadCard({ load, truck, onStage, isLoading, settings, index }) {
    const signal = getLoadSignal(load, truck, settings);
    const SignalIcon = signal.icon;
    
    const maxDaysToPay = settings?.max_broker_days_to_pay || 30;
    const minCreditScore = settings?.min_broker_credit_score || 90;
    const creditRisk = getRiskLevel(load.broker_credit_score, minCreditScore, true);
    const daysRisk = getRiskLevel(load.broker_days_to_pay, maxDaysToPay, false);
    
    const ratePerMile = load.distance > 0 ? (load.gross_pay / load.distance).toFixed(2) : '--';
    
    return (
        <div 
            className="p-3 rounded-lg border border-zinc-700/50 bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors"
            data-testid={`alternate-load-${index}-${load.load_id}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-xs font-medium">ALT {index}</span>
                    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${signal.bg}`}>
                        <SignalIcon className={`w-2.5 h-2.5 ${signal.color}`} />
                        <span className={`text-xs ${signal.color}`}>{signal.signal}</span>
                    </div>
                    <LoadScoreBadge load={load} />
                    <span className="text-zinc-300 font-mono text-xs">{load.load_id?.slice(-8)}</span>
                </div>
                <span className="text-green-400 font-semibold">{formatCurrency(load.gross_pay)}</span>
            </div>
            
            {/* Route - Compact */}
            <div className="flex items-center gap-1 text-sm text-zinc-400 mb-2">
                <span className="text-zinc-200">{load.origin_city}, {load.origin_state}</span>
                <ArrowRight className="w-3 h-3" />
                <span className="text-zinc-200">{load.destination_city}, {load.destination_state}</span>
            </div>
            
            {/* Metrics Row */}
            <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
                <span>{load.distance} mi</span>
                <span>${ratePerMile}/mi</span>
                <span>{load.broker_name}</span>
                <div className="flex gap-1">
                    <Badge className={`${creditRisk.color} text-xs px-1 py-0`}>{load.broker_credit_score}</Badge>
                    <Badge className={`${daysRisk.color} text-xs px-1 py-0`}>{load.broker_days_to_pay}d</Badge>
                </div>
            </div>
            
            {/* Compact Market Rate Info */}
            {(() => {
                const marketData = calculateMarketRate(load);
                return (
                    <div className="flex items-center justify-between text-xs mb-2 px-2 py-1.5 bg-zinc-900/50 rounded border border-zinc-700/30">
                        <span className="text-zinc-500">Mkt: <span className="text-white">${marketData.marketRpm.toFixed(2)}</span></span>
                        <span className={`flex items-center gap-0.5 ${marketData.isAboveMarket ? 'text-green-400' : 'text-red-400'}`}>
                            {marketData.isAboveMarket ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {marketData.difference >= 0 ? '+' : ''}{marketData.difference.toFixed(2)}
                        </span>
                        <span className="text-zinc-500">Target: <span className="text-amber-400">${marketData.negotiationTarget.toFixed(2)}</span></span>
                    </div>
                );
            })()}
            
            {/* Stage Button */}
            <Button
                size="sm"
                variant="outline"
                onClick={() => onStage(load.load_id, truck.truck_id)}
                disabled={isLoading}
                className="w-full border-purple-600 text-purple-400 hover:bg-purple-500/10 text-xs h-8"
                data-testid={`stage-alt-${truck.truck_id}-${load.load_id}`}
            >
                {isLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                ) : (
                    <ArrowRight className="w-3 h-3 mr-1" />
                )}
                Stage → Operations
            </Button>
        </div>
    );
}

// Suggested Load Card Component - Only shows policy-compliant loads
function SuggestedLoadCard({ load, truck, onBook, isBooking, settings }) {
    const signal = getLoadSignal(load, truck, settings);
    const SignalIcon = signal.icon;
    
    const maxDaysToPay = settings?.max_broker_days_to_pay || 30;
    const minCreditScore = settings?.min_broker_credit_score || 90;
    const creditRisk = getRiskLevel(load.broker_credit_score, minCreditScore, true);
    const daysRisk = getRiskLevel(load.broker_days_to_pay, maxDaysToPay, false);
    
    // Calculate rate per mile
    const ratePerMile = load.distance > 0 ? (load.gross_pay / load.distance).toFixed(2) : '--';
    
    return (
        <div 
            className={`p-3 rounded-lg border ${signal.bg}`}
            data-testid={`suggested-load-${load.load_id}`}
        >
            {/* Header with Signal and Load ID */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${signal.bg}`}>
                        <SignalIcon className={`w-3 h-3 ${signal.color}`} />
                        <span className={`text-xs font-semibold ${signal.color}`}>{signal.label || signal.signal}</span>
                    </div>
                    <span className="text-white font-mono text-sm">{load.load_id?.slice(-8)}</span>
                </div>
                {/* Policy compliant indicator */}
                <Tooltip>
                    <TooltipTrigger>
                        <Shield className="w-4 h-4 text-green-500" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-zinc-800 border-zinc-700">
                        <p className="text-green-400 text-xs font-semibold">Policy Compliant</p>
                        <p className="text-xs">Credit: {load.broker_credit_score || '--'} (min {minCreditScore})</p>
                        <p className="text-xs">Days: {load.broker_days_to_pay || '--'} (max {maxDaysToPay})</p>
                    </TooltipContent>
                </Tooltip>
            </div>
            
            {/* Route Info */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Pickup */}
                <div className="bg-zinc-900/50 rounded p-2">
                    <div className="flex items-center gap-1 text-green-400 text-xs mb-1">
                        <MapPin className="w-3 h-3" />
                        <span>PICKUP</span>
                    </div>
                    <p className="text-white text-sm font-medium">{load.origin_city}, {load.origin_state}</p>
                    <p className="text-zinc-400 text-xs flex items-center gap-1 mt-1">
                        <Calendar className="w-3 h-3" />
                        {load.pickup_date || '--'} {load.pickup_time || ''}
                    </p>
                </div>
                {/* Delivery */}
                <div className="bg-zinc-900/50 rounded p-2">
                    <div className="flex items-center gap-1 text-blue-400 text-xs mb-1">
                        <MapPin className="w-3 h-3" />
                        <span>DELIVERY</span>
                    </div>
                    <p className="text-white text-sm font-medium">{load.destination_city}, {load.destination_state}</p>
                    <p className="text-zinc-400 text-xs flex items-center gap-1 mt-1">
                        <Calendar className="w-3 h-3" />
                        {load.delivery_date || '--'} {load.delivery_time || ''}
                    </p>
                </div>
            </div>
            
            {/* Metrics Grid */}
            <div className="grid grid-cols-4 gap-2 mb-3 text-xs">
                <div className="text-center">
                    <p className="text-zinc-500">Miles</p>
                    <p className="text-white font-medium">{load.distance || '--'}</p>
                </div>
                <div className="text-center">
                    <p className="text-zinc-500">Deadhead</p>
                    <p className="text-white font-medium">{load.original_deadhead_miles?.toFixed(0) || '--'}</p>
                </div>
                <div className="text-center">
                    <p className="text-zinc-500">Rate</p>
                    <p className="text-green-400 font-medium">{formatCurrency(load.gross_pay)}</p>
                </div>
                <div className="text-center">
                    <p className="text-zinc-500">$/Mile</p>
                    <p className="text-green-400 font-medium">${ratePerMile}</p>
                </div>
            </div>
            
            {/* Broker Info */}
            <div className="flex items-center justify-between py-2 border-t border-zinc-700/50 mb-3">
                <div className="flex items-center gap-2">
                    <Building2 className="w-3 h-3 text-zinc-500" />
                    <span className="text-zinc-300 text-sm truncate max-w-[150px]">{load.broker_name || 'Unknown Broker'}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className={`${creditRisk.color} text-xs`}>
                        {load.broker_credit_score || '--'}
                    </Badge>
                    <Badge className={`${daysRisk.color} text-xs`}>
                        {load.broker_days_to_pay || '--'}d
                    </Badge>
                </div>
            </div>
            
            {/* ETAs */}
            {(load.original_estimated_pickup_eta || load.original_estimated_delivery_eta) && (
                <div className="flex items-center gap-4 text-xs text-zinc-400 mb-3">
                    {load.original_estimated_pickup_eta && (
                        <span className="flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            ETA Pickup: {formatDateTime(load.original_estimated_pickup_eta)}
                        </span>
                    )}
                    {load.original_estimated_delivery_eta && (
                        <span className="flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            ETA Delivery: {formatDateTime(load.original_estimated_delivery_eta)}
                        </span>
                    )}
                </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-2">
                <Button
                    size="sm"
                    onClick={() => onBook(load.load_id, truck.truck_id)}
                    disabled={isBooking}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                    data-testid={`book-${truck.truck_id}-${load.load_id}`}
                >
                    {isBooking ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                        <BookCheck className="w-3 h-3 mr-1" />
                    )}
                    Book to This Truck
                </Button>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                        >
                            <Info className="w-3 h-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-zinc-800 border-zinc-700">
                        <p className="text-xs">Negotiation Info (Coming Soon)</p>
                    </TooltipContent>
                </Tooltip>
            </div>
        </div>
    );
}

// Truck Priority Card for Panel 1
function TruckPriorityCard({ 
    truck, 
    rank,
    suggestedLoads, 
    settings,
    onStageLoad, 
    bookingLoading,
    hasHotLoad
}) {
    return (
        <div 
            className={`bg-zinc-900/50 rounded-xl border ${hasHotLoad ? 'border-orange-500 ring-2 ring-orange-500/30' : 'border-zinc-800'} overflow-hidden`}
            data-testid={`priority-truck-${truck.truck_id}`}
        >
            {/* Hot Load Alert Banner */}
            {hasHotLoad && (
                <div className="bg-orange-500/20 border-b border-orange-500/30 px-4 py-2 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-orange-500 animate-pulse" />
                    <span className="text-orange-400 text-sm font-semibold">HOT LOAD AVAILABLE!</span>
                </div>
            )}
            
            {/* Truck Header */}
            <div className="p-4 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center font-bold text-lg">
                            {rank}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <Truck className="w-5 h-5 text-orange-500" />
                                <span className="text-white font-semibold text-lg">{truck.truck_name}</span>
                            </div>
                            <p className="text-zinc-400 text-sm">{truck.driver_name}</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <Badge className={`${getTierColor(truck.priority_tier)} text-xs mb-1`}>
                            {truck.priority_tier}
                        </Badge>
                        <p className="text-zinc-500 text-xs">{truck.current_status}</p>
                    </div>
                </div>
                
                {/* Truck Stats */}
                <div className="grid grid-cols-3 gap-4 mt-4">
                    {truck.hours_empty > 0 && (
                        <div className="text-center">
                            <p className="text-amber-400 text-lg font-semibold">{truck.hours_empty?.toFixed(1)}h</p>
                            <p className="text-zinc-500 text-xs">Empty</p>
                        </div>
                    )}
                    {truck.revenue_gap > 0 && (
                        <div className="text-center">
                            <p className="text-red-400 text-lg font-semibold">{formatCurrency(truck.revenue_gap)}</p>
                            <p className="text-zinc-500 text-xs">Revenue Gap</p>
                        </div>
                    )}
                    <div className="text-center">
                        <p className="text-blue-400 text-lg font-semibold">{(truck.priority_score * 100).toFixed(0)}%</p>
                        <p className="text-zinc-500 text-xs">Priority</p>
                    </div>
                </div>
            </div>
            
            {/* Suggested Loads */}
            <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    <span className="text-zinc-300 font-medium">Load Recommendations</span>
                </div>
                
                {suggestedLoads.length === 0 ? (
                    <div className="text-center py-6 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500/70" />
                        <p className="text-amber-400 text-sm font-medium">No policy-compliant loads found.</p>
                        <p className="text-zinc-500 text-xs mt-1">Searching for better market options.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <TooltipProvider>
                            {/* Best Load - First item, highlighted */}
                            {suggestedLoads[0] && (
                                <BestLoadCard
                                    load={suggestedLoads[0]}
                                    truck={truck}
                                    onStage={onStageLoad}
                                    isLoading={bookingLoading[`book-${truck.truck_id}-${suggestedLoads[0].load_id}`]}
                                    settings={settings}
                                />
                            )}
                            
                            {/* Alternate Loads */}
                            {suggestedLoads.length > 1 && (
                                <div className="space-y-2">
                                    <p className="text-zinc-500 text-xs font-medium uppercase tracking-wide">Alternates</p>
                                    {suggestedLoads.slice(1).map((load, idx) => (
                                        <AlternateLoadCard
                                            key={load.load_id}
                                            load={load}
                                            truck={truck}
                                            onStage={onStageLoad}
                                            isLoading={bookingLoading[`book-${truck.truck_id}-${load.load_id}`]}
                                            settings={settings}
                                            index={idx + 1}
                                        />
                                    ))}
                                </div>
                            )}
                        </TooltipProvider>
                    </div>
                )}
            </div>
        </div>
    );
}

// Truck Monitoring Card for Panel 2 - Shows ALL trucks with full load queue
function TruckMonitorCard({ truck, loadData, hosState, actionLoading, onAdvanceStatus, onStartReset, onEndReset }) {
    // loadData = { activeLoad, bookedLoads: [], totalPlanned }
    const activeLoad = loadData?.activeLoad;
    const bookedLoads = loadData?.bookedLoads || [];
    
    // Reset timer state
    const [resetTimer, setResetTimer] = useState('00:00:00');
    const [resetElapsedHours, setResetElapsedHours] = useState(0);
    const [resetInProgress, setResetInProgress] = useState(false);
    const [resetStartTime, setResetStartTime] = useState(null);
    
    // Check reset status and update timer
    useEffect(() => {
        if (hosState?.reset_in_progress) {
            setResetInProgress(true);
            setResetStartTime(hosState.reset_start_time);
        } else {
            setResetInProgress(false);
            setResetStartTime(null);
            setResetElapsedHours(0);
        }
    }, [hosState]);
    
    // Update reset timer every second
    useEffect(() => {
        if (!resetInProgress || !resetStartTime) {
            setResetTimer('00:00:00');
            setResetElapsedHours(0);
            return;
        }
        
        const updateTimer = () => {
            try {
                const start = new Date(resetStartTime);
                const now = new Date();
                const elapsed = Math.floor((now - start) / 1000);
                const hours = Math.floor(elapsed / 3600);
                const minutes = Math.floor((elapsed % 3600) / 60);
                const seconds = elapsed % 60;
                setResetTimer(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
                setResetElapsedHours(elapsed / 3600); // Track elapsed hours for threshold checks
            } catch (e) {
                setResetTimer('00:00:00');
                setResetElapsedHours(0);
            }
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [resetInProgress, resetStartTime]);
    
    const getTruckStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'available': return 'bg-green-500';
            case 'booked': return 'bg-blue-500';
            case 'dispatched': return 'bg-purple-500';
            case 'in_transit': return 'bg-orange-500';
            default: return 'bg-zinc-500';
        }
    };
    
    const getLoadStatusColor = (status) => {
        switch (status) {
            case 'booked_pending_confirmation': return 'bg-blue-400';
            case 'booked': return 'bg-blue-500';
            case 'dispatched': return 'bg-purple-500';
            case 'at_pickup': return 'bg-amber-500';
            case 'in_transit': return 'bg-green-500';
            case 'at_delivery': return 'bg-orange-500';
            case 'delivered': return 'bg-emerald-600';
            default: return 'bg-zinc-500';
        }
    };
    
    const getDutyColor = (status) => {
        switch (status) {
            case 'driving': return 'text-green-400 border-green-600';
            case 'on_duty': return 'text-amber-400 border-amber-600';
            case 'off_duty': return 'text-zinc-400 border-zinc-600';
            case 'sleeper': return 'text-blue-400 border-blue-600';
            default: return 'text-zinc-400 border-zinc-600';
        }
    };

    // canAdvance: Allow advancing until COMPLETED or CANCELLED
    // Also allow advancing the NEXT BOOKED load when there's no active load
    const canAdvanceActive = activeLoad && !['completed', 'cancelled'].includes(activeLoad.status);
    const nextBookedLoad = bookedLoads.length > 0 ? bookedLoads[0] : null;
    const canAdvanceNextBooked = !activeLoad && nextBookedLoad;
    
    // Determine which load the Advance button should operate on
    const canAdvance = canAdvanceActive || canAdvanceNextBooked;
    const loadToAdvance = canAdvanceActive ? activeLoad : nextBookedLoad;
    const advanceButtonLabel = canAdvanceNextBooked ? 'Dispatch Next' : 'Advance';
    
    const truckName = truck.name || truck.truck_name;
    const driverName = truck.driver_name;
    const truckStatus = truck.status || 'available';

    return (
        <div 
            className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700"
            data-testid={`monitor-truck-${truck.truck_id}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-orange-500" />
                    <span className="text-white font-medium">{truckName}</span>
                </div>
                <Badge className={`${getTruckStatusColor(truckStatus)} text-xs uppercase`}>
                    {truckStatus?.replace(/_/g, ' ')}
                </Badge>
            </div>
            
            {/* Driver Name */}
            <p className="text-zinc-400 text-sm mb-3">{driverName}</p>

            {/* Load Queue - Shows current active + booked loads */}
            <div className="bg-zinc-900/50 rounded p-2 mb-3 max-h-64 overflow-y-auto">
                {/* Current Active Load */}
                {activeLoad ? (
                    <div className="space-y-2 mb-3">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-orange-400 uppercase">Current Load</span>
                            <Badge className={`${getLoadStatusColor(activeLoad.status)} text-xs`}>
                                {activeLoad.status?.replace(/_/g, ' ')}
                            </Badge>
                        </div>
                        {/* Route */}
                        <div className="flex items-center gap-1 text-sm">
                            <MapPin className="w-3 h-3 text-green-500" />
                            <span className="text-white">{activeLoad.origin_city}, {activeLoad.origin_state}</span>
                            <ArrowRight className="w-3 h-3 text-zinc-500" />
                            <MapPin className="w-3 h-3 text-red-500" />
                            <span className="text-white">{activeLoad.destination_city}, {activeLoad.destination_state}</span>
                        </div>
                        
                        {/* Broker & Rate */}
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">{activeLoad.broker_name || 'Unknown Broker'}</span>
                            <span className="text-green-400 font-semibold">${activeLoad.gross_pay?.toLocaleString() || '---'}</span>
                        </div>
                        
                        {/* ETAs */}
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-800">
                            <div>
                                <p className="text-zinc-500 text-xs">Pickup ETA</p>
                                <p className="text-white text-xs font-medium">
                                    {activeLoad.status === 'at_pickup' ? 'At Location' :
                                     activeLoad.status === 'in_transit' || activeLoad.status === 'at_delivery' || activeLoad.status === 'delivered' ? 'Complete' :
                                     calculatePickupETA(activeLoad, hosState)}
                                </p>
                            </div>
                            <div>
                                <p className="text-zinc-500 text-xs">Delivery ETA</p>
                                <p className="text-white text-xs font-medium">
                                    {activeLoad.status === 'at_delivery' ? 'At Location' :
                                     activeLoad.status === 'delivered' ? 'Complete' :
                                     calculateDeliveryETA(activeLoad, hosState)}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-2 mb-2">
                        <span className="text-zinc-500 text-sm">No Active Load</span>
                    </div>
                )}
                
                {/* Booked/Future Loads Queue */}
                {bookedLoads.length > 0 && (
                    <div className="border-t border-zinc-700 pt-2 space-y-2">
                        <span className="text-xs font-semibold text-blue-400 uppercase">
                            Upcoming Loads ({bookedLoads.length})
                        </span>
                        {bookedLoads.map((load, index) => (
                            <div key={load.load_id} className="bg-zinc-800/50 rounded p-2 border border-zinc-700/50">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-zinc-500 text-xs">
                                        {index === 0 ? 'Next' : `#${index + 1}`}
                                    </span>
                                    <Badge className={`${getLoadStatusColor(load.status)} text-xs`}>
                                        {load.status === 'booked_pending_confirmation' ? 'Staged' : load.status?.replace(/_/g, ' ')}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1 text-xs">
                                    <MapPin className="w-3 h-3 text-green-500" />
                                    <span className="text-white">{load.origin_city}, {load.origin_state}</span>
                                    <ArrowRight className="w-3 h-3 text-zinc-500" />
                                    <MapPin className="w-3 h-3 text-red-500" />
                                    <span className="text-white">{load.destination_city}, {load.destination_state}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs mt-1">
                                    <span className="text-zinc-500">{load.broker_name || 'Unknown'}</span>
                                    <span className="text-green-400">${load.gross_pay?.toLocaleString() || '---'}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* HOS Status */}
            <div className="bg-zinc-900/50 rounded p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        HOS Status
                    </span>
                    <Badge variant="outline" className={`text-xs ${getDutyColor(hosState?.duty_status)}`}>
                        {hosState?.duty_status?.replace(/_/g, ' ') || 'off duty'}
                    </Badge>
                </div>
                
                {/* Reset Timer - Visible when driver is in reset */}
                {resetInProgress && (
                    <div className="bg-blue-900/30 border border-blue-700/50 rounded p-2 mb-2">
                        <div className="flex items-center justify-between">
                            <span className="text-blue-400 text-xs font-medium">Reset In Progress</span>
                            <span className="text-blue-300 font-mono text-lg" data-testid={`reset-timer-${truck.truck_id}`}>{resetTimer}</span>
                        </div>
                        <div className="flex gap-1 mt-1">
                            {resetElapsedHours >= 10 ? (
                                <Badge className="bg-green-600/50 text-green-300 text-[9px]">10hr ✓</Badge>
                            ) : (
                                <Badge className="bg-zinc-600/50 text-zinc-400 text-[9px]">10hr: {(10 - resetElapsedHours).toFixed(1)}h left</Badge>
                            )}
                            {resetElapsedHours >= 34 ? (
                                <Badge className="bg-purple-600/50 text-purple-300 text-[9px]">34hr ✓</Badge>
                            ) : (
                                <Badge className="bg-zinc-600/50 text-zinc-400 text-[9px]">34hr: {(34 - resetElapsedHours).toFixed(1)}h left</Badge>
                            )}
                        </div>
                    </div>
                )}
                
                <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                        <p className="text-zinc-500 text-xs">Drive</p>
                        <p className="text-white font-medium">{formatHours(hosState?.drive_hours_remaining ?? 11)}</p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">On-Duty</p>
                        <p className="text-white font-medium">{formatHours(hosState?.on_duty_window_remaining ?? 14)}</p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Cycle</p>
                        <p className="text-white font-medium">{formatHours(hosState?.cycle_hours_remaining ?? 70)}</p>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-zinc-700">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={!canAdvance || actionLoading[`advance-${truck.truck_id}`]}
                                onClick={() => onAdvanceStatus(loadToAdvance?.load_id, truck.truck_id)}
                                className={`${canAdvanceNextBooked ? 'border-green-700 text-green-400 hover:bg-green-900/30' : 'border-orange-700 text-orange-400 hover:bg-orange-900/30'} text-xs`}
                                data-testid={`advance-${truck.truck_id}`}
                            >
                                {actionLoading[`advance-${truck.truck_id}`] ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                    <FastForward className="w-3 h-3" />
                                )}
                                <span className="ml-1">{advanceButtonLabel}</span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-zinc-800 border-zinc-700">
                            <p>{canAdvanceNextBooked 
                                ? `Dispatch next booked load: ${nextBookedLoad?.origin_city} → ${nextBookedLoad?.destination_city}`
                                : 'Advance load to next status'
                            }</p>
                        </TooltipContent>
                    </Tooltip>

                    {/* Start/End Reset Buttons */}
                    {!resetInProgress ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={actionLoading[`reset-${truck.truck_id}`]}
                                    onClick={() => onStartReset(truck.truck_id)}
                                    className="border-blue-700 text-blue-400 hover:bg-blue-900/30 text-xs"
                                    data-testid={`start-reset-${truck.truck_id}`}
                                >
                                    {actionLoading[`reset-${truck.truck_id}`] ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <Timer className="w-3 h-3" />
                                    )}
                                    <span className="ml-1">Start Reset</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-zinc-800 border-zinc-700">
                                <p>Start driver reset timer (HOS clocks pause)</p>
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={actionLoading[`reset-${truck.truck_id}`]}
                                    onClick={() => onEndReset(truck.truck_id)}
                                    className="border-green-700 text-green-400 hover:bg-green-900/30 text-xs"
                                    data-testid={`end-reset-${truck.truck_id}`}
                                >
                                    {actionLoading[`reset-${truck.truck_id}`] ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <CheckCircle className="w-3 h-3" />
                                    )}
                                    <span className="ml-1">End Reset</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-zinc-800 border-zinc-700">
                                <p>End reset and apply HOS rules (≥10h: daily clocks, ≥34h: all clocks)</p>
                            </TooltipContent>
                        </Tooltip>
                    )}
                </TooltipProvider>
            </div>
        </div>
    );
}

export default function FleetVisibilityPage() {
    const [priorityStack, setPriorityStack] = useState([]);
    const [availableLoads, setAvailableLoads] = useState([]);
    const [activeTrucks, setActiveTrucks] = useState([]);
    const [activeLoads, setActiveLoads] = useState({});
    const [hosStates, setHosStates] = useState({});
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    const [bookingLoading, setBookingLoading] = useState({});
    const [hotLoads, setHotLoads] = useState(new Set());
    const [expandedPanel, setExpandedPanel] = useState(null); // 'priority' | 'monitoring' | null
    const [truckPriorityLoads, setTruckPriorityLoads] = useState({}); // Per-truck priority loads from API
    
    // Refs for smart refresh
    const previousLoadsRef = useRef({});

    // Fetch all data
    const fetchData = useCallback(async (isAutoRefresh = false) => {
        if (!isAutoRefresh) setLoading(true);
        
        try {
            const [priorityRes, loadsRes, trucksRes, settingsRes] = await Promise.all([
                fetch(`${API_URL}/api/priority-stack`, { credentials: 'include' }),
                fetch(`${API_URL}/api/loads?status=available`, { credentials: 'include' }),
                fetch(`${API_URL}/api/trucks`, { credentials: 'include' }),
                fetch(`${API_URL}/api/settings`, { credentials: 'include' })
            ]);

            if (priorityRes.ok) {
                const data = await priorityRes.json();
                setPriorityStack(data.priority_stack || []);
            }
            
            if (loadsRes.ok) {
                const loadsData = await loadsRes.json();
                
                // Smart refresh: only update if better loads appear
                if (isAutoRefresh) {
                    const newLoads = loadsData.filter(load => {
                        const prevLoad = previousLoadsRef.current[load.load_id];
                        return !prevLoad || load.gross_pay > prevLoad.gross_pay;
                    });
                    
                    // Check for new HOT loads (must pass policy)
                    const newHotSet = new Set();
                    loadsData.forEach(load => {
                        const policy = checkPolicyViolation(load, settings);
                        const dailyTarget = (settings?.weekly_target || 6500) / 7;
                        if (!policy.hasViolation && load.gross_pay >= dailyTarget * 1.2) {
                            if (!previousLoadsRef.current[load.load_id]) {
                                newHotSet.add(load.load_id);
                            }
                        }
                    });
                    
                    if (newHotSet.size > 0) {
                        setHotLoads(prev => new Set([...prev, ...newHotSet]));
                        toast.success('🔥 HOT Load Available!', {
                            description: `${newHotSet.size} new high-value load(s) found`
                        });
                    }
                }
                
                // Update refs
                previousLoadsRef.current = {};
                loadsData.forEach(load => {
                    previousLoadsRef.current[load.load_id] = load;
                });
                
                setAvailableLoads(loadsData);
            }
            
            if (trucksRes.ok) {
                const trucksData = await trucksRes.json();
                
                // Fetch ALL loads (active + booked) for each truck to show full queue
                // Use Promise.allSettled to ensure all requests complete without blocking
                const truckDataPromises = trucksData.map(async (truck) => {
                    try {
                        // Fetch the truck's load chain (active + booked loads)
                        const loadChainRes = await fetch(`${API_URL}/api/trucks/${truck.truck_id}/load-chain`, { credentials: 'include' });
                        if (loadChainRes.ok) {
                            const loadChainData = await loadChainRes.json();
                            return { 
                                truckId: truck.truck_id,
                                type: 'loadChain',
                                data: {
                                    activeLoad: loadChainData.active_load,
                                    bookedLoads: loadChainData.booked_loads || [],
                                    totalPlanned: loadChainData.total_planned_loads || 0
                                }
                            };
                        } else {
                            // Fallback: try to get single active load
                            const loadId = truck.current_load_id || truck.active_load_id;
                            if (loadId) {
                                const loadRes = await fetch(`${API_URL}/api/loads/${loadId}`, { credentials: 'include' });
                                if (loadRes.ok) {
                                    const loadData = await loadRes.json();
                                    return {
                                        truckId: truck.truck_id,
                                        type: 'loadChain',
                                        data: {
                                            activeLoad: loadData,
                                            bookedLoads: [],
                                            totalPlanned: 1
                                        }
                                    };
                                }
                            }
                        }
                    } catch (err) {
                        console.error(`Failed to fetch loads for truck ${truck.truck_id}:`, err);
                    }
                    return null;
                });
                
                const hosPromises = trucksData.map(async (truck) => {
                    try {
                        const hosRes = await fetch(`${API_URL}/api/hos-simulation/duty-state/${truck.truck_id}`, { credentials: 'include' });
                        if (hosRes.ok) {
                            const hosData = await hosRes.json();
                            return { truckId: truck.truck_id, type: 'hos', data: hosData };
                        }
                    } catch (err) {
                        console.error(`Failed to fetch HOS for truck ${truck.truck_id}:`, err);
                    }
                    return null;
                });
                
                // Wait for all truck data to resolve (but don't fail if some error out)
                const [loadChainResults, hosResults] = await Promise.all([
                    Promise.allSettled(truckDataPromises),
                    Promise.allSettled(hosPromises)
                ]);
                
                // Process load chain results
                const newActiveLoads = {};
                loadChainResults.forEach(result => {
                    if (result.status === 'fulfilled' && result.value) {
                        newActiveLoads[result.value.truckId] = result.value.data;
                    }
                });
                setActiveLoads(prev => ({ ...prev, ...newActiveLoads }));
                
                // Process HOS results
                const newHosStates = {};
                hosResults.forEach(result => {
                    if (result.status === 'fulfilled' && result.value) {
                        newHosStates[result.value.truckId] = result.value.data;
                    }
                });
                setHosStates(prev => ({ ...prev, ...newHosStates }));
                
                // Fetch PRIORITY LOADS for each truck from the Priority API
                // This ensures proper distance filtering and scoring per truck
                // Fetch for ALL trucks to show load recommendations even for booked trucks
                const priorityLoadsPromises = trucksData
                    .map(async (truck) => {
                        try {
                            const priorityRes = await fetch(
                                `${API_URL}/api/priority/loads?truck_id=${truck.truck_id}&limit=3`, 
                                { credentials: 'include' }
                            );
                            if (priorityRes.ok) {
                                const priorityData = await priorityRes.json();
                                // Combine priority and consider loads, take top 3
                                const allLoads = [
                                    ...(priorityData.priority || []),
                                    ...(priorityData.consider || [])
                                ].slice(0, 3);
                                return { truckId: truck.truck_id, loads: allLoads };
                            }
                        } catch (err) {
                            console.error(`Failed to fetch priority loads for truck ${truck.truck_id}:`, err);
                        }
                        return { truckId: truck.truck_id, loads: [] };
                    });
                
                const priorityLoadsResults = await Promise.allSettled(priorityLoadsPromises);
                const newTruckPriorityLoads = {};
                priorityLoadsResults.forEach(result => {
                    if (result.status === 'fulfilled' && result.value) {
                        newTruckPriorityLoads[result.value.truckId] = result.value.loads;
                    }
                });
                
                // Set priority loads FIRST, then trucks - this ensures the data is ready when trucks render
                setTruckPriorityLoads(newTruckPriorityLoads);
                setActiveTrucks(trucksData);
            }
            
            if (settingsRes.ok) {
                const settingsData = await settingsRes.json();
                setSettings(settingsData);
            }
            
            setLastRefresh(new Date());
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    }, [settings]);

    useEffect(() => {
        fetchData(false);
        // Auto-refresh every 60 seconds
        const interval = setInterval(() => fetchData(true), 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    // Get suggested loads for a truck (top 3) - Uses Priority API for proper distance filtering
    const getSuggestedLoads = (truck) => {
        // Use pre-fetched priority loads from the API (already filtered by distance, equipment, broker policy)
        const priorityLoads = truckPriorityLoads[truck.truck_id];
        
        if (priorityLoads && priorityLoads.length > 0) {
            // Priority API returns loads with nested priority_score object
            return priorityLoads.slice(0, 3).map(load => {
                const scoreData = load.priority_score || {};
                return {
                    ...load,
                    _score: scoreData.total_score || load.score || 0,
                    _scoreBreakdown: scoreData.components || {},
                    _decision: scoreData.decision || 'CONSIDER',
                    _priority: scoreData.priority || 'CONSIDER',
                    _summary: scoreData.summary || '',
                    _keyReasons: scoreData.key_reasons || [],
                    _keyRisks: scoreData.key_risks || [],
                    _rpm: load.rate_per_mile || (load.gross_pay / (load.total_miles || 1)),
                    _hubScore: load.destination_reload_score || scoreData.components?.find(c => c.name === 'Market Strength')?.score || 0
                };
            });
        }
        
        // If priority data exists but is empty, return empty array (no nearby loads)
        if (truckPriorityLoads.hasOwnProperty(truck.truck_id)) {
            return [];
        }
        
        // If priority data is still loading (not yet fetched), return empty for now
        // This prevents showing incorrect loads from the global pool
        return [];
    };
    
    // Check if truck has a HOT load (already policy-compliant from getSuggestedLoads)
    const truckHasHotLoad = (truck) => {
        const suggested = getSuggestedLoads(truck);
        const dailyTarget = (truck.weekly_target_revenue || 6500) / 7;
        return suggested.some(load => load.gross_pay >= dailyTarget * 1.2);
    };

    // Handle staging - sends load to Operations queue for negotiation workflow
    const handleStageLoad = async (loadId, truckId) => {
        const loadingKey = `book-${truckId}-${loadId}`;
        setBookingLoading(prev => ({ ...prev, [loadingKey]: true }));
        
        try {
            const response = await fetch(`${API_URL}/api/loads/${loadId}/stage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ truck_id: truckId })
            });
            
            if (response.ok) {
                const data = await response.json();
                toast.success('Load staged for Operations', {
                    description: `Go to Operations page to negotiate and book. Queue position: ${data.queue_position + 1}`
                });
                setHotLoads(prev => {
                    const next = new Set(prev);
                    next.delete(loadId);
                    return next;
                });
                fetchData(false);
            } else {
                let errorDetail = 'Unknown error';
                try {
                    const error = await response.json();
                    if (error.detail?.error_type === 'policy_block') {
                        errorDetail = error.detail.message;
                    } else if (typeof error.detail === 'string') {
                        errorDetail = error.detail;
                    } else if (error.detail?.message) {
                        errorDetail = error.detail.message;
                    }
                } catch (e) {
                    errorDetail = `HTTP ${response.status}`;
                }
                toast.error('Staging failed', { description: errorDetail });
            }
        } catch (error) {
            toast.error('Staging failed', { description: error.message });
        } finally {
            setBookingLoading(prev => ({ ...prev, [loadingKey]: false }));
        }
    };

    // Handle booking - same endpoint as "Book to Truck" on My Loads page
    const handleBookLoad = async (loadId, truckId) => {
        const loadingKey = `book-${truckId}-${loadId}`;
        setBookingLoading(prev => ({ ...prev, [loadingKey]: true }));
        
        try {
            // Use XMLHttpRequest for reliable response handling (same as BookingModal)
            const url = `${API_URL}/api/loads/${loadId}/confirm`;
            
            const data = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', url, true);
                xhr.setRequestHeader('Content-Type', 'application/json');
                xhr.withCredentials = true;
                
                xhr.onload = function() {
                    let responseData = null;
                    try {
                        responseData = JSON.parse(xhr.responseText);
                    } catch (e) {
                        responseData = { detail: xhr.responseText || `HTTP ${xhr.status}` };
                    }
                    resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data: responseData });
                };
                
                xhr.onerror = function() {
                    reject(new Error('Network error'));
                };
                
                xhr.send(JSON.stringify({ truck_id: truckId }));
            });
            
            if (data.ok) {
                toast.success('Load booked successfully!', {
                    description: `Load assigned to truck and ready for dispatch.`
                });
                setHotLoads(prev => {
                    const next = new Set(prev);
                    next.delete(loadId);
                    return next;
                });
                fetchData(false);
            } else {
                // Parse error for policy blocks
                let errorDetail = 'Unknown error';
                const errorData = data.data;
                if (errorData?.detail?.error_type === 'policy_block') {
                    errorDetail = errorData.detail.message;
                } else if (typeof errorData?.detail === 'string') {
                    errorDetail = errorData.detail;
                } else if (errorData?.detail?.message) {
                    errorDetail = errorData.detail.message;
                } else if (errorData?.message) {
                    errorDetail = errorData.message;
                }
                toast.error('Booking failed', { description: errorDetail });
            }
        } catch (error) {
            toast.error('Booking failed', { description: error.message });
        } finally {
            setBookingLoading(prev => ({ ...prev, [loadingKey]: false }));
        }
    };

    // Action handlers
    const handleAdvanceStatus = async (loadId, truckId) => {
        console.log('[Advance] Starting advance for load:', loadId, 'truck:', truckId);
        if (!loadId) {
            toast.error('No load selected', { description: 'Please select a load to advance' });
            return;
        }
        setActionLoading(prev => ({ ...prev, [`advance-${truckId}`]: true }));
        try {
            console.log('[Advance] Fetching:', `${API_URL}/api/simulation/advance-status/${loadId}`);
            const response = await fetch(`${API_URL}/api/simulation/advance-status/${loadId}`, {
                method: 'POST',
                credentials: 'include'
            });
            
            console.log('[Advance] Response status:', response.status);
            
            // Get response text first (can only read body once)
            const responseText = await response.text();
            console.log('[Advance] Response text length:', responseText.length);
            
            // Try to parse as JSON
            let responseData;
            try {
                responseData = JSON.parse(responseText);
                console.log('[Advance] Parsed JSON successfully');
            } catch (parseError) {
                console.log('[Advance] JSON parse failed:', parseError.message);
                // Not valid JSON
                if (response.ok) {
                    toast.success('Status advanced');
                    await fetchData(false);
                } else {
                    toast.error('Failed to advance status', { description: responseText || `HTTP ${response.status}` });
                }
                return;
            }
            
            if (response.ok) {
                // Check if next load was auto-activated
                if (responseData.next_load_activated) {
                    toast.success('Load completed & next load activated', {
                        description: `${responseData.previous_status} → ${responseData.new_status}. Next: ${responseData.next_load_activated.origin} → ${responseData.next_load_activated.destination}`
                    });
                } else {
                    toast.success('Status advanced', {
                        description: `${responseData.previous_status} → ${responseData.new_status}`
                    });
                }
                
                // Immediate refresh to update the truck's load chain
                await fetchData(false);
            } else {
                // Handle structured error types
                const error = responseData;
                if (error.detail?.error_type) {
                    const errorType = error.detail.error_type;
                    const message = error.detail.message || 'Unknown error';
                    
                    if (errorType === 'compliance_block') {
                        toast.error('Safety Compliance Block', { description: message, duration: 10000 });
                        error.detail.failures?.forEach(f => {
                            toast.warning(`${f.field}: ${f.reason}`, { duration: 8000 });
                        });
                    } else if (errorType === 'ratecon_required') {
                        toast.error('Rate Confirmation Required', { description: message, duration: 8000 });
                    } else if (errorType === 'ratecon_unsigned') {
                        toast.error('Rate Confirmation Not Signed', { description: message, duration: 8000 });
                    } else if (errorType === 'policy_block') {
                        toast.error('Policy Block', { description: message, duration: 8000 });
                    } else {
                        toast.error('Failed to advance status', { description: message });
                    }
                } else {
                    const errorMessage = typeof error.detail === 'string' ? error.detail : error.detail?.message || 'Unknown error';
                    toast.error('Failed to advance status', { description: errorMessage });
                }
            }
        } catch (error) {
            toast.error('Failed to advance status', { description: error.message });
        } finally {
            setActionLoading(prev => ({ ...prev, [`advance-${truckId}`]: false }));
        }
    };

    const handleStartReset = async (truckId) => {
        setActionLoading(prev => ({ ...prev, [`reset-${truckId}`]: true }));
        try {
            const response = await fetch(`${API_URL}/api/hos-simulation/duty-state/${truckId}/start-reset`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const result = await response.json();
                toast.success('Reset started - driver is now OFF DUTY', {
                    description: `Timer started at ${new Date(result.reset_start_time).toLocaleTimeString()}`
                });
                fetchData(false);
            } else {
                const error = await response.json();
                toast.error('Failed to start reset', { description: error.detail || 'Unknown error' });
            }
        } catch (error) {
            toast.error('Failed to start reset', { description: error.message });
        } finally {
            setActionLoading(prev => ({ ...prev, [`reset-${truckId}`]: false }));
        }
    };

    const handleEndReset = async (truckId) => {
        setActionLoading(prev => ({ ...prev, [`reset-${truckId}`]: true }));
        try {
            const response = await fetch(`${API_URL}/api/hos-simulation/duty-state/${truckId}/end-reset`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const result = await response.json();
                const resetType = result.reset_type;
                let description = '';
                if (resetType === '34hr') {
                    description = 'All clocks reset (drive, duty, and cycle)';
                } else if (resetType === '10hr') {
                    description = 'Daily clocks reset (drive and on-duty)';
                } else {
                    description = 'Reset too short - no clock changes applied';
                }
                toast.success(`Reset ended - ${result.reset_duration_hours?.toFixed(1) || '0'}h recorded`, {
                    description
                });
                fetchData(false);
            } else {
                const error = await response.json();
                toast.error('Failed to end reset', { description: error.detail || 'Unknown error' });
            }
        } catch (error) {
            toast.error('Failed to end reset', { description: error.message });
        } finally {
            setActionLoading(prev => ({ ...prev, [`reset-${truckId}`]: false }));
        }
    };

    return (
        <div className="p-6 space-y-6" data-testid="command-center-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Target className="w-7 h-7 text-orange-500" />
                        Command Center
                    </h1>
                    <p className="text-zinc-400 text-sm mt-1">
                        Main operational dispatch screen
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    {lastRefresh && (
                        <span className="text-xs text-zinc-500">
                            Updated: {lastRefresh.toLocaleTimeString()}
                        </span>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchData(false)}
                        disabled={loading}
                        className="border-zinc-700 text-zinc-300"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Main Content - Two Panel Layout */}
            <div className={`grid gap-6 ${expandedPanel ? '' : 'grid-cols-1 xl:grid-cols-3'}`}>
                {/* PANEL 1 - Truck Priority + Load Selection */}
                {(!expandedPanel || expandedPanel === 'priority') && (
                    <div className={`space-y-4 ${expandedPanel === 'priority' ? 'fixed inset-2 sm:inset-4 z-50 bg-zinc-950 rounded-xl p-4 overflow-hidden flex flex-col' : 'xl:col-span-2'}`}>
                        <div className="flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <Activity className="w-5 h-5 text-orange-500" />
                                <h2 className="text-lg font-semibold text-white">Truck Priority + Load Selection</h2>
                                <Badge variant="outline" className="ml-2 border-zinc-700 text-zinc-400">
                                    {priorityStack.length} trucks
                                </Badge>
                                {expandedPanel === 'priority' && (
                                    <Badge className="bg-orange-600/50 text-orange-300 text-xs ml-2">Expanded View</Badge>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedPanel(expandedPanel === 'priority' ? null : 'priority')}
                                className="text-zinc-400 hover:text-white"
                                data-testid="expand-priority-btn"
                            >
                                {expandedPanel === 'priority' ? (
                                    <Minimize2 className="w-4 h-4" />
                                ) : (
                                    <Maximize2 className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        
                        {loading && priorityStack.length === 0 ? (
                            <div className="flex items-center justify-center py-20">
                                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                            </div>
                        ) : priorityStack.length === 0 ? (
                            <Card className="bg-zinc-900/50 border-zinc-800">
                                <CardContent className="py-20 text-center">
                                    <Truck className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                                    <p className="text-zinc-500">No trucks in the system</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className={`space-y-6 ${expandedPanel === 'priority' ? 'flex-1 overflow-y-auto pr-2' : ''}`}>
                                {priorityStack.map((truck, index) => {
                                    // Get priority loads for this specific truck from state
                                    const truckLoads = truckPriorityLoads[truck.truck_id] || [];
                                    const suggestedLoads = truckLoads.slice(0, 3).map(load => {
                                        const scoreData = load.priority_score || {};
                                        return {
                                            ...load,
                                            _score: scoreData.total_score || load.score || 0,
                                            _scoreBreakdown: scoreData.components || {},
                                            _decision: scoreData.decision || 'CONSIDER',
                                            _priority: scoreData.priority || 'CONSIDER',
                                            _summary: scoreData.summary || '',
                                            _keyReasons: scoreData.key_reasons || [],
                                            _keyRisks: scoreData.key_risks || [],
                                            _rpm: load.rate_per_mile || (load.gross_pay / (load.total_miles || 1)),
                                            _hubScore: load.destination_reload_score || scoreData.components?.find(c => c.name === 'Market Strength')?.score || 0
                                        };
                                    });
                                    
                                    return (
                                        <TruckPriorityCard
                                            key={truck.truck_id}
                                            truck={truck}
                                            rank={index + 1}
                                            suggestedLoads={suggestedLoads}
                                            settings={settings}
                                            onStageLoad={handleStageLoad}
                                            bookingLoading={bookingLoading}
                                            hasHotLoad={suggestedLoads.some(load => {
                                                const dailyTarget = (truck.weekly_target_revenue || 6500) / 7;
                                                return load.gross_pay >= dailyTarget * 1.2;
                                            })}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* PANEL 2 - Truck Monitoring */}
                {(!expandedPanel || expandedPanel === 'monitoring') && (
                    <div className={`space-y-4 ${expandedPanel === 'monitoring' ? 'fixed inset-2 sm:inset-4 z-50 bg-zinc-950 rounded-xl p-4 overflow-hidden flex flex-col' : ''}`}>
                        <div className="flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <Activity className="w-5 h-5 text-green-500" />
                                <h2 className="text-lg font-semibold text-white">Truck Monitoring</h2>
                                <Badge variant="outline" className="ml-2 border-zinc-700 text-zinc-400">
                                    {activeTrucks.length} trucks
                                </Badge>
                                {expandedPanel === 'monitoring' && (
                                    <Badge className="bg-green-600/50 text-green-300 text-xs ml-2">Expanded View</Badge>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedPanel(expandedPanel === 'monitoring' ? null : 'monitoring')}
                                className="text-zinc-400 hover:text-white"
                                data-testid="expand-monitoring-btn"
                            >
                                {expandedPanel === 'monitoring' ? (
                                    <Minimize2 className="w-4 h-4" />
                                ) : (
                                    <Maximize2 className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        
                        {loading && activeTrucks.length === 0 ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                            </div>
                        ) : (
                            <div className={`space-y-4 pr-2 ${expandedPanel === 'monitoring' ? 'flex-1 overflow-y-auto' : 'max-h-[calc(100vh-200px)] overflow-y-auto'}`}>
                                {activeTrucks.map((truck) => (
                                    <TruckMonitorCard
                                        key={truck.truck_id}
                                        truck={truck}
                                        loadData={activeLoads[truck.truck_id]}
                                        hosState={hosStates[truck.truck_id]}
                                        actionLoading={actionLoading}
                                        onAdvanceStatus={handleAdvanceStatus}
                                        onStartReset={handleStartReset}
                                        onEndReset={handleEndReset}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Backdrop when expanded */}
            {expandedPanel && (
                <div 
                    className="fixed inset-0 bg-black/80 z-40"
                    onClick={() => setExpandedPanel(null)}
                />
            )}
        </div>
    );
}
